import { useEffect } from "react";
import { prefetchIdleRoutes, prefetchRoute } from "@/lib/prefetchRoute";

/**
 * Global, delegated prefetcher.
 *
 * Watches the document for hover/focus on internal anchor tags and warms up
 * the corresponding route chunk before the user clicks. Also kicks off a
 * background prefetch of the highest-traffic routes once the browser is idle.
 *
 * Zero per-link changes required — works with every <Link> and <a> in the app.
 */
export const RoutePrefetcher = () => {
  useEffect(() => {
    const seen = new WeakSet<Element>();

    const tryPrefetch = (el: Element | null) => {
      if (!el) return;
      const anchor = el.closest("a");
      if (!anchor) return;
      if (seen.has(anchor)) return;
      const href = anchor.getAttribute("href");
      if (!href) return;
      if (href.startsWith("http") || href.startsWith("mailto:") || href.startsWith("tel:") || href.startsWith("#")) return;
      seen.add(anchor);
      try {
        const url = new URL(anchor.href, window.location.href);
        if (url.origin !== window.location.origin) return;
        prefetchRoute(url.pathname);
      } catch {
        // ignore
      }
    };

    const onPointer = (e: Event) => tryPrefetch(e.target as Element | null);

    document.addEventListener("mouseover", onPointer, { passive: true });
    document.addEventListener("focusin", onPointer);
    document.addEventListener("touchstart", onPointer, { passive: true });

    // Idle-prefetch the most common destinations from the landing page.
    prefetchIdleRoutes([
      "/services",
      "/ai-investing-club",
      "/about",
      "/login",
    ]);

    return () => {
      document.removeEventListener("mouseover", onPointer);
      document.removeEventListener("focusin", onPointer);
      document.removeEventListener("touchstart", onPointer);
    };
  }, []);

  return null;
};
